import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, map, of, switchMap } from 'rxjs';
import {
  institutoParamDtoToCopiaCommand,
  institutoParamDtoToInstitutoPlano,
  institutoPlanoToPatchCommand,
} from '../../mappers/parametros/instituto-param-api.mapper';
import { InstitutoParamResponseDto } from '../../models/parametros/instituto-param-api.model';
import { InstitutosPlano } from '../../models/parametros/plano-institutos.model';
import { InstitutosParamApiService } from './institutos-param-api.service';
import { PlanosService } from './planos.service';
import { ResgateConfiguracaoService } from './resgate-configuracao.service';

/**
 * Institutos de cada plano (aba "Instituto" da parametrização).
 * Os institutos associados vêm de `/v1/institutos-param`; o prazo de opção e a combinação de
 * institutos são o `nr_dias_prazo_opc` e o `fl_permite_comb_inst` do plano (`/v1/planos-param`).
 * Um instituto entra no plano ao ser associado e configurado ("Associar Instituto" → seleção →
 * configuração; hoje só o Resgate tem tela).
 */
@Injectable({
  providedIn: 'root',
})
export class PlanoInstitutosService {
  private readonly institutosParamApiService = inject(InstitutosParamApiService);
  private readonly planosService = inject(PlanosService);
  private readonly resgateService = inject(ResgateConfiguracaoService);

  obterInstitutos(planoId: number): Observable<InstitutosPlano> {
    return forkJoin({
      institutos: this.institutosParamApiService.listarInstitutosParam({ id_plano: planoId }),
      opcoes: this.planosService.obterOpcoesInstitutos(planoId),
    }).pipe(
      map(({ institutos, opcoes }) => ({
        institutos: institutos.map(institutoParamDtoToInstitutoPlano),
        ...opcoes,
      })),
    );
  }

  /** Grava o status e a disponibilidade de cada instituto e o prazo de opção e a combinação do plano. */
  salvarInstitutos(planoId: number, dados: InstitutosPlano): Observable<InstitutosPlano> {
    const institutos = dados.institutos.map((instituto) =>
      this.institutosParamApiService.atualizarInstitutoParam(instituto.id, institutoPlanoToPatchCommand(instituto)),
    );
    const opcoes = this.planosService.alterarOpcoesInstitutos(planoId, {
      prazoOpcaoDias: dados.prazoOpcaoDias,
      permitirCombinacao: dados.permitirCombinacao,
    });
    return forkJoin([opcoes, ...institutos]).pipe(switchMap(() => this.obterInstitutos(planoId)));
  }

  /**
   * Plano duplicado: associa à cópia cada instituto do plano de origem (status, vigência e
   * disponibilidade), com a configuração do resgate. O prazo de opção já vem no clone do plano.
   */
  copiarInstitutos(planoOrigem: number, planoDestino: number): Observable<void> {
    return this.institutosParamApiService.listarInstitutosParam({ id_plano: planoOrigem }).pipe(
      switchMap((institutos) =>
        institutos.length ? forkJoin(institutos.map((instituto) => this.copiarInstituto(instituto, planoDestino))) : of([]),
      ),
      map(() => undefined),
    );
  }

  private copiarInstituto(origem: InstitutoParamResponseDto, planoDestino: number): Observable<void> {
    return this.institutosParamApiService
      .criarInstitutoParam(institutoParamDtoToCopiaCommand(origem, planoDestino))
      .pipe(switchMap((copia) => this.resgateService.copiarResgate(origem.id, copia.id)));
  }
}
